import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  Legend,
  LinearScale,
  Tooltip,
  TooltipItem,
} from 'chart.js';
import React, { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import { TBarChartsCapitalPerYear } from './types.d';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);
const barGradientPlugin = {
  id: 'barGradientPlugin',
  beforeDatasetsDraw: (chart: ChartJS<'bar'>) => {
    const { ctx, chartArea, data } = chart;
    if (!chartArea) return;

    const gradients = [
      ['#A8D8EA', '#7EC8E3'],
      ['#FFD3B6', '#FFBE9F'],
      ['#D5AAFF', '#C490E4'],
      ['#FF9AA2', '#FF8090'],
      ['#B5EAD7', '#9ADBC4'],
      ['#C7CEEA', '#ACB5DB'],
      ['#FFDAC1', '#FFC9A8'],
    ];

    data.datasets[0].backgroundColor = data?.labels?.map((_: unknown, index: number) => {
      const [from, to] = gradients[index % gradients.length];
      const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);

      gradient.addColorStop(0, from);
      gradient.addColorStop(1, to);

      return gradient;
    });
  },
};

const BarChartsCapitalPerYear: React.FC<TBarChartsCapitalPerYear> = ({
  portfolioCapitalPerYear,
}) => {
  const data = useMemo(
    () => ({
      labels: portfolioCapitalPerYear?.map((x) => x.periodo),
      datasets: [
        {
          label: 'Créditos otorgados',
          data: portfolioCapitalPerYear?.map((x) => x.total),
          borderRadius: 16,
          maxBarThickness: 56,
          borderWidth: 1.5,
          borderColor: 'rgba(0,0,0,0.08)',
        },
      ],
    }),
    [portfolioCapitalPerYear]
  );

  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    animation: {
      duration: 1200,
      easing: 'easeOutCubic',
      delay(ctx: { dataIndex: number }) {
        return ctx.dataIndex * 80;
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#1e3a5f',
        borderColor: '#64b5f6',
        borderWidth: 1,
        padding: 14,
        titleFont: { weight: 'bold' },
        bodyFont: { size: 13 },
        callbacks: {
          label(tooltipItem: TooltipItem<'bar'>) {
            const raw = tooltipItem.raw as number | null;
            return raw == null ? '' : ` ${Number(raw).toLocaleString('es-CO')} créditos`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          color: '#94a3b8',
          font: { size: 12 },
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(148,163,184,0.35)',
          lineWidth: 1.4,
        },
        ticks: {
          color: '#94a3b8',
          font: { size: 12 },
        },
      },
    },
  };

  return <Bar data={data} options={options} plugins={[barGradientPlugin]} />;
};

export default BarChartsCapitalPerYear;
